// components/BidChatBubble.tsx

import {
  BidDecision,
  DecisionActionEnum,
  DecisionEnum,
  User,
  UserOnEntity,
} from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { ChatBubbleBase } from "../../Components";
import { JSX } from "react";

interface Props {
  bidDecision: BidDecision;
  isFirstItem?: boolean;
  tenderUsers: UserOnEntity[];
};

const BidChatBubble = ({ bidDecision, isFirstItem, tenderUsers }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);

  /**
   * Returns the appropriate title text prefix based on the provided decision.
   *
   * @param {BidDecision} decision - The decision object containing the current decision and action.
   * @returns {string} The title text prefix based on the decision and action.
   */
  const getTitleTextPrefix = (decision: BidDecision): string => {
    // If it's a comment, return empty string
    if (decision.action === DecisionActionEnum.Comment) {
      return "";
    }
    const currentDecision = decision.decision;
    if (decision.action === DecisionActionEnum.Overrule) {
      if (currentDecision === DecisionEnum.Bid) {
        return t("chatbubble.overrulebid");
      } else {
        return t("chatbubble.overrulenobid");
      }
    } else if (isFirstItem) {
      if (currentDecision === DecisionEnum.Bid) {
        return t("chatbubble.bid");
      } else {
        return t("chatbubble.nobid");
      }
    } else if (decision.action === DecisionActionEnum.Flip) {
      if (currentDecision === DecisionEnum.Bid) {
        return t("chatbubble.disagreedbid");
      } else {
        return t("chatbubble.disagreednobid");
      }
    } else if (decision.action === DecisionActionEnum.Approve) {
      if (currentDecision === DecisionEnum.Bid) {
        return t("chatbubble.agreedbid");
      } else {
        return t("chatbubble.agreednobid");
      }
    }
    return "";
  };

  /**
   * Returns the CSS class for the border color based on the bid decision.
   *
   * @param {BidDecision} decision - The bid decision object containing the decision type.
   * @returns {string} The CSS class for the border color.
   */
  const getDecisionColour = (decision: BidDecision): string => {
    if(decision.action === DecisionActionEnum.Comment) {
      return "";
    }
    if (decision.decision === DecisionEnum.Bid) {
      return "border-green-500";
    } else if (decision.decision === DecisionEnum.Nobid) {
      return "border-red-500";
    }
    return "";
  };

  /**
   * Returns the background color based on the bid decision.
   * @param {BidDecision} decision - The bid decision object containing the decision type.
   * @returns {string} The background color based on the bid decision.
   */
  const getBackgroundColour = (decision: BidDecision): string => {
    if (decision.action === DecisionActionEnum.Overrule) {
      if (decision.decision === DecisionEnum.Bid) {
        return "bg-green-100";
      }
      return "bg-red-100";
    }
    return "";
  };

  // Getter functions
  const getComment = (decision: BidDecision): string => decision.comment ?? "";
  const getDate = (decision: BidDecision): string => decision.createdAt;
  const getUser = (decision: BidDecision): User => decision.userOnDecision as User;
  const getUserName = (decision: BidDecision): string => {
    const firstname = decision.userOnDecision?.firstname ?? "";
    const lastname = decision.userOnDecision?.lastname ?? "";
    return `${firstname} ${lastname}`.trim();
  };
  const getOrganizationName = (decision: BidDecision): string =>
    decision.userOnDecision?.organization?.name ?? "";

  return (
    <ChatBubbleBase
      decision={bidDecision}
      getDecisionColour={getDecisionColour}
      getBackgroundColour={getBackgroundColour}
      getTitleTextPrefix={(decision) =>
        getTitleTextPrefix(decision as BidDecision)
      }
      getComment={getComment}
      getDate={getDate}
      getUserName={getUserName}
      getOrganizationName={getOrganizationName}
      getUser={getUser}
      tenderUsers={tenderUsers}
    />
  );
};

export default BidChatBubble;
